<script setup>
import {
  computed,
  nextTick,
  onActivated,
  onMounted,
  reactive,
  ref,
  watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { onBeforeRouteLeave, useRoute, useRouter } from 'vue-router';
import { debounce } from '@chatwoot/utils';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import CampaignCenterAPI from '../api';
import {
  LARGE_AUDIENCE,
  buildSegment,
  formatDateTime,
  formatDuration,
  formatNumber,
  fromLocalInputValue,
  segmentToFilters,
  toLocalInputValue,
} from '../helpers';

import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import WhatsAppTemplateParser from 'dashboard/components-next/whatsapp/WhatsAppTemplateParser.vue';
import AudienceBuilder from '../components/AudienceBuilder.vue';
import AudiencePreviewCard from '../components/AudiencePreviewCard.vue';
import SendSettingsForm from '../components/SendSettingsForm.vue';
import MessagePreview from '../components/MessagePreview.vue';
import TestSendDialog from '../components/TestSendDialog.vue';
import StatusBadge from '../components/StatusBadge.vue';

const props = defineProps({
  channel: { type: String, required: true },
  campaignId: { type: [String, Number], default: null },
});

const { t, locale } = useI18n();
const route = useRoute();
const router = useRouter();

const whatsappInboxes = useMapGetter('inboxes/getWhatsAppInboxes');
const smsInboxes = useMapGetter('inboxes/getSMSInboxes');
const getFilteredWhatsAppTemplates = useMapGetter(
  'inboxes/getFilteredWhatsAppTemplates'
);

const isWhatsApp = computed(() => props.channel === 'whatsapp');
const channelInboxes = computed(
  () => (isWhatsApp.value ? whatsappInboxes.value : smsInboxes.value) || []
);

const form = reactive({
  title: '',
  description: '',
  inboxId: null,
  templateKey: null,
  message: '',
  labelIds: [],
  filters: [],
  settings: {
    rate_per_minute: 20,
    window_enabled: false,
    window_start: 8,
    window_end: 18,
    conversation_status: 'pending',
  },
  scheduleMode: 'now',
  scheduledAt: '',
});

const campaign = ref(null); // saved record (edit mode / after first save)
const accountSettings = ref({
  limits: { max_rate_per_minute: 200 },
  timezone: 'UTC',
  default_rate_per_minute: 20,
});
const isLoading = ref(true);
const isSaving = ref(false);
const submitted = ref(false);
const audiencePreview = ref(null);
const isPreviewLoading = ref(false);
const messagePreview = ref(null);
const previewContactId = ref(null);
const templateParserRef = ref(null);
const audienceBuilderRef = ref(null);
const settingsFormRef = ref(null);
const confirmDialogRef = ref(null);
const testDialogRef = ref(null);
const leaveDialogRef = ref(null);
const pendingTemplateParams = ref(null);

// The page lives inside the campaigns keep-alive router view: (re)initialise per route, not per mount.
let loadedPath = null;

// ---------- unsaved changes ----------
// Snapshot of everything the user edits (form + template variables); refreshed on load and save.
const savedSignature = ref('');
const dirtySignature = () =>
  JSON.stringify({
    form,
    params: templateParserRef.value?.processedParams || null,
  });
const isDirty = () => savedSignature.value !== dirtySignature();
let pendingLeave = null;
onBeforeRouteLeave((to, from, next) => {
  if (!isDirty()) {
    next();
    return;
  }
  pendingLeave = next;
  leaveDialogRef.value?.open();
});
const confirmLeave = () => {
  const proceed = pendingLeave;
  pendingLeave = null;
  leaveDialogRef.value?.close();
  // the user chose to discard: the kept-alive page must start fresh on the next visit
  loadedPath = null;
  proceed?.();
};
const cancelLeave = () => {
  const stay = pendingLeave;
  pendingLeave = null;
  stay?.(false);
};

// ---------- derived ----------
const inboxOptions = computed(() =>
  channelInboxes.value.map(inbox => ({ value: inbox.id, label: inbox.name }))
);
const selectedInbox = computed(
  () => channelInboxes.value.find(inbox => inbox.id === form.inboxId) || null
);
const templates = computed(() =>
  form.inboxId && isWhatsApp.value
    ? getFilteredWhatsAppTemplates.value(form.inboxId) || []
    : []
);
const templateKeyOf = template =>
  `${template.name}::${template.language || ''}`;
const templateOptions = computed(() =>
  templates.value.map(template => ({
    value: templateKeyOf(template),
    label: `${template.name.replace(/_/g, ' ')} (${template.language || 'en'})`,
  }))
);
const selectedTemplate = computed(
  () =>
    templates.value.find(
      template => templateKeyOf(template) === form.templateKey
    ) || null
);
const templateParams = computed(() => {
  if (!isWhatsApp.value || !selectedTemplate.value) return {};
  const template = selectedTemplate.value;
  return {
    name: template.name,
    namespace: template.namespace || '',
    category: template.category || 'UTILITY',
    language: template.language || 'en_US',
    processed_params: templateParserRef.value?.processedParams || {},
  };
});
const renderedMessage = computed(() =>
  isWhatsApp.value
    ? templateParserRef.value?.renderedTemplate || ''
    : form.message
);
// the template parser validates required variables (header media, body/button params); it must be valid before scheduling
const templateVariablesValid = computed(
  () =>
    !isWhatsApp.value ||
    (!!selectedTemplate.value &&
      templateParserRef.value?.v$?.$invalid === false)
);
const locked = computed(() => !!campaign.value?.started);
const hasAudience = computed(
  () => form.labelIds.length > 0 || form.filters.length > 0
);
const segment = computed(() => buildSegment(form.filters));
const audiencePayload = computed(() =>
  form.labelIds.map(id => ({ type: 'Label', id }))
);
const settingsValid = computed(() => settingsFormRef.value?.isValid !== false);
const scheduledAtIso = computed(() =>
  form.scheduleMode === 'later' ? fromLocalInputValue(form.scheduledAt) : null
);
const scheduleInPast = computed(
  () =>
    form.scheduleMode === 'later' &&
    form.scheduledAt &&
    new Date(form.scheduledAt) < new Date()
);
const smsParts = computed(() =>
  Math.max(1, Math.ceil((form.message || '').length / 160))
);

const errors = computed(() => {
  const list = {};
  if (!form.title.trim()) list.title = true;
  if (!form.inboxId) list.inbox = true;
  if (isWhatsApp.value) {
    if (!selectedTemplate.value) list.template = true;
    else if (!templateVariablesValid.value) list.templateVariables = true;
  } else if (!form.message.trim()) list.message = true;
  if (!hasAudience.value) list.audience = true;
  if (!settingsValid.value) list.settings = true;
  if (form.scheduleMode === 'later' && !form.scheduledAt) list.schedule = true;
  return list;
});
const canSaveDraft = computed(() => form.title.trim() && form.inboxId);
const readyToSchedule = computed(() => Object.keys(errors.value).length === 0);
const showError = key => submitted.value && !!errors.value[key];

const triggerRules = () => ({
  wasapi: {
    rate_per_minute: form.settings.rate_per_minute,
    window_start: form.settings.window_enabled
      ? form.settings.window_start
      : null,
    window_end: form.settings.window_enabled ? form.settings.window_end : null,
    conversation_status: form.settings.conversation_status,
    segment: segment.value,
  },
});
const buildPayload = () => ({
  title: form.title.trim(),
  description: form.description,
  inbox_id: form.inboxId,
  message:
    renderedMessage.value ||
    (isWhatsApp.value ? selectedTemplate.value?.name || '' : ''),
  template_params: isWhatsApp.value ? templateParams.value : {},
  audience: audiencePayload.value,
  trigger_rules: triggerRules(),
  scheduled_at: scheduledAtIso.value,
});

// ---------- previews ----------
const refreshAudiencePreview = async () => {
  if (!hasAudience.value) {
    audiencePreview.value = null;
    return;
  }
  if (audienceBuilderRef.value && !audienceBuilderRef.value.validate()) return;
  isPreviewLoading.value = true;
  try {
    const { data } = await CampaignCenterAPI.audiencePreview(null, {
      audience: audiencePayload.value,
      segment: segment.value,
      rate_per_minute: form.settings.rate_per_minute,
      window_start: form.settings.window_enabled
        ? form.settings.window_start
        : null,
      window_end: form.settings.window_enabled
        ? form.settings.window_end
        : null,
      scheduled_at: scheduledAtIso.value,
    });
    audiencePreview.value = data;
    if (!previewContactId.value && data.sample?.length)
      previewContactId.value = data.sample[0].id;
  } catch (error) {
    useAlert(error?.response?.data?.error || t('WASAPI_CAMPAIGNS.TOAST.ERROR'));
  } finally {
    isPreviewLoading.value = false;
  }
};
const debouncedAudiencePreview = debounce(refreshAudiencePreview, 600, false);

const refreshMessagePreview = async () => {
  const hasContent = isWhatsApp.value
    ? !!selectedTemplate.value
    : !!form.message.trim();
  if (!form.inboxId || !hasContent) {
    messagePreview.value = null;
    return;
  }
  try {
    const { data } = await CampaignCenterAPI.messagePreview(null, {
      campaign: {
        inbox_id: form.inboxId,
        message: renderedMessage.value || form.message,
        template_params: isWhatsApp.value ? templateParams.value : {},
        audience: audiencePayload.value,
        trigger_rules: { wasapi: { segment: segment.value } },
      },
      contactId: previewContactId.value,
    });
    messagePreview.value = data;
  } catch {
    messagePreview.value = null;
  }
};
const debouncedMessagePreview = debounce(refreshMessagePreview, 600, false);

watch(
  () => [
    form.labelIds,
    form.filters,
    form.settings.rate_per_minute,
    form.settings.window_enabled,
    form.settings.window_start,
    form.settings.window_end,
    form.scheduleMode,
    form.scheduledAt,
  ],
  () => debouncedAudiencePreview(),
  { deep: true }
);
watch(
  () => [
    form.message,
    form.templateKey,
    templateParserRef.value?.processedParams,
    previewContactId.value,
    form.inboxId,
  ],
  () => debouncedMessagePreview(),
  { deep: true }
);
watch(
  () => form.inboxId,
  (next, prev) => {
    if (prev !== null && prev !== next) form.templateKey = null;
  }
);
// inboxes load asynchronously: pre-select the only inbox of the channel for new campaigns
watch(
  channelInboxes,
  list => {
    if (!props.campaignId && !form.inboxId && list.length === 1) {
      const wasClean = !isDirty();
      form.inboxId = list[0].id;
      // pre-selecting the only inbox is not a user edit
      if (wasClean) savedSignature.value = dirtySignature();
    }
  },
  { immediate: true }
);
// restore saved template variables once the parser mounted for the saved template
watch(
  () => templateParserRef.value,
  parser => {
    if (parser && pendingTemplateParams.value) {
      nextTick(() => {
        parser.processedParams = pendingTemplateParams.value;
        pendingTemplateParams.value = null;
        // restoring saved variables is not a user edit
        savedSignature.value = dirtySignature();
      });
    }
  }
);

// ---------- load ----------
const applyCampaign = record => {
  campaign.value = record;
  form.title = record.title || '';
  form.description = record.description || '';
  form.inboxId = record.inbox?.id || null;
  form.message = record.message || '';
  form.labelIds = (record.audience || [])
    .filter(a => a.type === 'Label')
    .map(a => a.id);
  form.filters = segmentToFilters(record.settings?.segment);
  const s = record.settings || {};
  form.settings = {
    rate_per_minute:
      s.rate_per_minute || accountSettings.value.default_rate_per_minute,
    window_enabled:
      s.window_start !== null &&
      s.window_start !== undefined &&
      s.window_end !== null &&
      s.window_end !== undefined,
    window_start: s.window_start ?? 8,
    window_end: s.window_end ?? 18,
    conversation_status: s.conversation_status || 'pending',
  };
  if (record.status === 'scheduled' && record.scheduled_at) {
    form.scheduleMode = 'later';
    form.scheduledAt = toLocalInputValue(record.scheduled_at);
  }
  if (isWhatsApp.value && record.template_params?.name) {
    form.templateKey = `${record.template_params.name}::${record.template_params.language || ''}`;
    pendingTemplateParams.value =
      record.template_params.processed_params || null;
  }
};

const DEFAULT_FORM = () => ({
  title: '',
  description: '',
  inboxId: null,
  templateKey: null,
  message: '',
  labelIds: [],
  filters: [],
  settings: {
    rate_per_minute: accountSettings.value.default_rate_per_minute || 20,
    window_enabled: false,
    window_start: 8,
    window_end: 18,
    conversation_status: 'pending',
  },
  scheduleMode: 'now',
  scheduledAt: '',
});

const init = async () => {
  loadedPath = route.fullPath;
  isLoading.value = true;
  campaign.value = null;
  audiencePreview.value = null;
  messagePreview.value = null;
  previewContactId.value = null;
  submitted.value = false;
  Object.assign(form, DEFAULT_FORM());
  try {
    const { data: settings } = await CampaignCenterAPI.settings();
    accountSettings.value = settings;
    form.settings.rate_per_minute = settings.default_rate_per_minute || 20;
    if (props.campaignId) {
      const { data } = await CampaignCenterAPI.get(props.campaignId);
      applyCampaign(data);
    } else if (channelInboxes.value.length === 1) {
      form.inboxId = channelInboxes.value[0].id;
    }
  } finally {
    isLoading.value = false;
  }
  savedSignature.value = dirtySignature();
  refreshAudiencePreview();
  refreshMessagePreview();
};
onMounted(init);
onActivated(() => {
  if (!loadedPath || loadedPath !== route.fullPath) init();
});

// ---------- actions ----------
const persist = async (status = 'draft') => {
  const payload = buildPayload();
  const response = campaign.value
    ? await CampaignCenterAPI.update(campaign.value.id, payload, status)
    : await CampaignCenterAPI.create(payload, status);
  campaign.value = response.data;
  savedSignature.value = dirtySignature();
  return response.data;
};
const errorMessage = error => {
  const data = error?.response?.data;
  if (data?.error) return data.error;
  if (data?.errors)
    return Array.isArray(data.errors)
      ? data.errors.join(', ')
      : JSON.stringify(data.errors);
  if (data?.message) return data.message;
  return t('WASAPI_CAMPAIGNS.TOAST.ERROR');
};
const goToDetail = id =>
  router.push({
    name: 'wasapi_campaign_detail',
    params: { channel: props.channel, campaignId: id },
  });
const goBack = () =>
  router.push({
    name: isWhatsApp.value ? 'campaigns_whatsapp_index' : 'campaigns_sms_index',
  });

const saveDraft = async ({ silent = false } = {}) => {
  if (!canSaveDraft.value) {
    submitted.value = true;
    return null;
  }
  isSaving.value = true;
  try {
    const isNew = !campaign.value;
    const saved = await persist(
      campaign.value?.status === 'scheduled' ? undefined : 'draft'
    );
    if (!silent) useAlert(t('WASAPI_CAMPAIGNS.TOAST.SAVED'));
    if (isNew) {
      await router.replace({
        name: 'wasapi_campaign_edit',
        params: { channel: props.channel, campaignId: saved.id },
      });
      loadedPath = route.fullPath;
    }
    return saved;
  } catch (error) {
    useAlert(errorMessage(error));
    return null;
  } finally {
    isSaving.value = false;
  }
};

const openTestDialog = async () => {
  const saved = await saveDraft({ silent: true });
  if (saved) testDialogRef.value.open();
};
const sendTest = async (phoneNumber, resolve, reject) => {
  try {
    const { data } = await CampaignCenterAPI.testSend(campaign.value.id, {
      phoneNumber,
    });
    useAlert(t('WASAPI_CAMPAIGNS.TOAST.TEST_SENT'), {
      type: 'link',
      to: {
        name: 'inbox_conversation',
        params: { conversation_id: data.conversation_id },
      },
      message: t('WASAPI_CAMPAIGNS.TEST.SENT'),
    });
    resolve();
  } catch (error) {
    useAlert(errorMessage(error));
    reject(error);
  }
};

const openScheduleConfirm = async () => {
  submitted.value = true;
  if (audienceBuilderRef.value && !audienceBuilderRef.value.validate()) return;
  if (!readyToSchedule.value) {
    useAlert(t('WASAPI_CAMPAIGNS.EDITOR.INVALID'));
    return;
  }
  await refreshAudiencePreview();
  confirmDialogRef.value.open();
};
const confirmSchedule = async () => {
  isSaving.value = true;
  try {
    const saved = await persist('scheduled');
    useAlert(t('WASAPI_CAMPAIGNS.TOAST.SCHEDULED'));
    confirmDialogRef.value.close();
    goToDetail(saved.id);
  } catch (error) {
    useAlert(errorMessage(error));
  } finally {
    isSaving.value = false;
  }
};

const summaryRows = computed(() => {
  const p = audiencePreview.value;
  const s = form.settings;
  return [
    ['CAMPAIGN', form.title],
    ['CHANNEL', t(`WASAPI_CAMPAIGNS.CHANNEL.${props.channel}`)],
    ['INBOX', selectedInbox.value?.name || '—'],
    ['AUDIENCE', p ? formatNumber(p.eligible, locale.value) : '—'],
    isWhatsApp.value
      ? ['TEMPLATE', selectedTemplate.value?.name || '—']
      : ['MESSAGE', (form.message || '').slice(0, 80)],
    [
      'START',
      form.scheduleMode === 'later' && form.scheduledAt
        ? formatDateTime(form.scheduledAt, locale.value)
        : t('WASAPI_CAMPAIGNS.SUMMARY.NOW'),
    ],
    ['TIMEZONE', accountSettings.value.timezone],
    [
      'RATE',
      t('WASAPI_CAMPAIGNS.SUMMARY.RATE_VALUE', { rate: s.rate_per_minute }),
    ],
    [
      'WINDOW',
      s.window_enabled
        ? t('WASAPI_CAMPAIGNS.SUMMARY.WINDOW_VALUE', {
            start: s.window_start,
            end: s.window_end,
          })
        : t('WASAPI_CAMPAIGNS.SUMMARY.WINDOW_NONE'),
    ],
    [
      'DURATION',
      p?.estimate ? formatDuration(p.estimate.duration_minutes, t) : '—',
    ],
    [
      'COMPLETION',
      p?.estimate && p.eligible
        ? formatDateTime(
            p.estimate.completion_at,
            locale.value,
            accountSettings.value.timezone
          )
        : '—',
    ],
  ];
});
const largeAudience = computed(
  () => (audiencePreview.value?.eligible || 0) >= LARGE_AUDIENCE
);
const previewContactOptions = computed(() =>
  (audiencePreview.value?.sample || []).map(c => ({
    value: c.id,
    label: `${c.name || c.phone_number}`,
  }))
);
</script>

<template>
  <section class="flex flex-col w-full h-full overflow-y-auto bg-n-surface-1">
    <div class="flex flex-col w-full max-w-6xl gap-5 px-6 py-6 mx-auto">
      <header class="flex flex-wrap items-center justify-between gap-3">
        <div class="flex items-center gap-3">
          <Button
            icon="i-lucide-arrow-left"
            variant="ghost"
            color="slate"
            size="sm"
            @click="goBack"
          />
          <div class="flex flex-col">
            <h1
              class="flex items-center gap-2 text-xl font-medium text-n-slate-12"
            >
              {{
                campaign
                  ? t('WASAPI_CAMPAIGNS.EDITOR.EDIT_TITLE')
                  : t('WASAPI_CAMPAIGNS.EDITOR.NEW_TITLE')
              }}
              <StatusBadge v-if="campaign" :status="campaign.status" />
            </h1>
            <p class="text-sm text-n-slate-11">
              {{ t(`WASAPI_CAMPAIGNS.CHANNEL.${channel}`) }}
            </p>
          </div>
        </div>
        <div class="flex items-center gap-2">
          <Button
            :label="t('WASAPI_CAMPAIGNS.ACTIONS.TEST_SEND')"
            icon="i-lucide-send"
            variant="faded"
            color="slate"
            size="sm"
            :disabled="isSaving || !canSaveDraft || locked"
            @click="openTestDialog"
          />
          <Button
            :label="
              campaign?.status === 'scheduled'
                ? t('WASAPI_CAMPAIGNS.ACTIONS.SAVE')
                : t('WASAPI_CAMPAIGNS.ACTIONS.SAVE_DRAFT')
            "
            variant="faded"
            color="slate"
            size="sm"
            :is-loading="isSaving"
            :disabled="!canSaveDraft || locked"
            @click="saveDraft()"
          />
          <Button
            :label="
              form.scheduleMode === 'now'
                ? t('WASAPI_CAMPAIGNS.ACTIONS.SEND_NOW')
                : t('WASAPI_CAMPAIGNS.ACTIONS.SCHEDULE')
            "
            icon="i-lucide-calendar-check"
            color="blue"
            size="sm"
            :disabled="isSaving || locked || campaign?.status === 'scheduled'"
            @click="openScheduleConfirm"
          />
        </div>
      </header>

      <div v-if="isLoading" class="flex justify-center py-16"><Spinner /></div>

      <div
        v-else
        class="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_22rem]"
      >
        <div class="flex flex-col gap-5">
          <p
            v-if="locked"
            class="p-3 text-sm rounded-lg bg-n-amber-3 text-n-amber-11"
          >
            {{ t('WASAPI_CAMPAIGNS.EDITOR.LOCKED_NOTICE') }}
          </p>

          <!-- 1. Channel & inbox -->
          <section
            id="campaign-step-channel"
            class="flex flex-col gap-4 p-5 rounded-xl outline outline-1 outline-n-weak bg-n-solid-1"
          >
            <h2 class="text-sm font-medium text-n-slate-12">
              {{ `1. ${t('WASAPI_CAMPAIGNS.EDITOR.STEPS.CHANNEL')}` }}
            </h2>
            <Input
              v-model="form.title"
              :label="t('WASAPI_CAMPAIGNS.EDITOR.TITLE_LABEL')"
              :placeholder="t('WASAPI_CAMPAIGNS.EDITOR.TITLE_PLACEHOLDER')"
              :message="
                showError('title') ? t('WASAPI_CAMPAIGNS.EDITOR.INVALID') : ''
              "
              :message-type="showError('title') ? 'error' : 'info'"
            />
            <Input
              v-model="form.description"
              :label="t('WASAPI_CAMPAIGNS.EDITOR.DESCRIPTION_LABEL')"
            />
            <div class="flex flex-col gap-1">
              <label class="text-sm font-medium text-n-slate-12">{{
                t('WASAPI_CAMPAIGNS.EDITOR.INBOX_LABEL')
              }}</label>
              <p v-if="!inboxOptions.length" class="text-sm text-n-ruby-11">
                {{ t('WASAPI_CAMPAIGNS.EDITOR.INBOX_NONE') }}
              </p>
              <ComboBox
                v-else
                v-model="form.inboxId"
                :options="inboxOptions"
                :placeholder="t('WASAPI_CAMPAIGNS.EDITOR.INBOX_PLACEHOLDER')"
                :disabled="locked"
                :has-error="showError('inbox')"
                class="[&>div>button]:bg-n-alpha-black2"
              />
            </div>
          </section>

          <!-- 2. Content -->
          <section
            id="campaign-step-content"
            class="flex flex-col gap-4 p-5 rounded-xl outline outline-1 outline-n-weak bg-n-solid-1"
          >
            <h2 class="text-sm font-medium text-n-slate-12">
              {{ `2. ${t('WASAPI_CAMPAIGNS.EDITOR.STEPS.CONTENT')}` }}
            </h2>
            <template v-if="isWhatsApp">
              <div class="flex flex-col gap-1">
                <label class="text-sm font-medium text-n-slate-12">{{
                  t('WASAPI_CAMPAIGNS.EDITOR.TEMPLATE_LABEL')
                }}</label>
                <p
                  v-if="form.inboxId && !templateOptions.length"
                  class="text-sm text-n-amber-11"
                >
                  {{ t('WASAPI_CAMPAIGNS.EDITOR.TEMPLATE_NONE') }}
                </p>
                <ComboBox
                  v-else
                  v-model="form.templateKey"
                  :options="templateOptions"
                  :placeholder="
                    t('WASAPI_CAMPAIGNS.EDITOR.TEMPLATE_PLACEHOLDER')
                  "
                  :disabled="locked || !form.inboxId"
                  :has-error="showError('template')"
                  class="[&>div>button]:bg-n-alpha-black2"
                />
              </div>
              <WhatsAppTemplateParser
                v-if="selectedTemplate"
                ref="templateParserRef"
                :template="selectedTemplate"
              />
              <p
                v-if="showError('templateVariables')"
                class="text-sm text-n-ruby-11"
              >
                {{ t('WASAPI_CAMPAIGNS.EDITOR.TEMPLATE_VARIABLES_REQUIRED') }}
              </p>
            </template>
            <template v-else>
              <TextArea
                v-model="form.message"
                :label="t('WASAPI_CAMPAIGNS.EDITOR.MESSAGE_LABEL')"
                :placeholder="t('WASAPI_CAMPAIGNS.EDITOR.MESSAGE_PLACEHOLDER')"
                :max-length="1600"
                :disabled="locked"
                auto-height
                :message="
                  showError('message')
                    ? t('WASAPI_CAMPAIGNS.EDITOR.INVALID')
                    : t('WASAPI_CAMPAIGNS.EDITOR.SMS_LENGTH', {
                        count: form.message.length,
                        parts: smsParts,
                      })
                "
                :message-type="showError('message') ? 'error' : 'info'"
              />
              <p class="text-xs text-n-slate-11">
                {{ t('WASAPI_CAMPAIGNS.EDITOR.MESSAGE_HELP') }}
              </p>
            </template>
          </section>

          <!-- 3. Audience -->
          <section
            id="campaign-step-audience"
            class="flex flex-col gap-4 p-5 rounded-xl outline outline-1 outline-n-weak bg-n-solid-1"
          >
            <h2 class="text-sm font-medium text-n-slate-12">
              {{ `3. ${t('WASAPI_CAMPAIGNS.EDITOR.STEPS.AUDIENCE')}` }}
            </h2>
            <AudienceBuilder
              ref="audienceBuilderRef"
              v-model:label-ids="form.labelIds"
              v-model:filters="form.filters"
              :disabled="locked"
            />
            <p v-if="showError('audience')" class="text-sm text-n-ruby-11">
              {{ t('WASAPI_CAMPAIGNS.AUDIENCE.NONE') }}
            </p>
          </section>

          <!-- 4. Delivery settings -->
          <section
            id="campaign-step-settings"
            class="flex flex-col gap-4 p-5 rounded-xl outline outline-1 outline-n-weak bg-n-solid-1"
          >
            <h2 class="text-sm font-medium text-n-slate-12">
              {{ `4. ${t('WASAPI_CAMPAIGNS.EDITOR.STEPS.SETTINGS')}` }}
            </h2>
            <SendSettingsForm
              ref="settingsFormRef"
              v-model="form.settings"
              :max-rate="accountSettings.limits?.max_rate_per_minute || 200"
              :timezone="accountSettings.timezone"
              :disabled="
                campaign &&
                ['completed', 'cancelled', 'failed'].includes(campaign.status)
              "
            />
          </section>

          <!-- 5. Schedule -->
          <section
            id="campaign-step-schedule"
            class="flex flex-col gap-4 p-5 rounded-xl outline outline-1 outline-n-weak bg-n-solid-1"
          >
            <h2 class="text-sm font-medium text-n-slate-12">
              {{ `5. ${t('WASAPI_CAMPAIGNS.EDITOR.STEPS.SCHEDULE')}` }}
            </h2>
            <div class="flex flex-wrap gap-2">
              <Button
                :label="t('WASAPI_CAMPAIGNS.SCHEDULE.NOW')"
                :variant="form.scheduleMode === 'now' ? 'solid' : 'faded'"
                color="slate"
                size="sm"
                :disabled="locked"
                @click="form.scheduleMode = 'now'"
              />
              <Button
                :label="t('WASAPI_CAMPAIGNS.SCHEDULE.LATER')"
                :variant="form.scheduleMode === 'later' ? 'solid' : 'faded'"
                color="slate"
                size="sm"
                :disabled="locked"
                @click="form.scheduleMode = 'later'"
              />
            </div>
            <Input
              v-if="form.scheduleMode === 'later'"
              v-model="form.scheduledAt"
              type="datetime-local"
              :label="t('WASAPI_CAMPAIGNS.SCHEDULE.AT_LABEL')"
              :disabled="locked"
              :message="
                showError('schedule')
                  ? t('WASAPI_CAMPAIGNS.EDITOR.INVALID')
                  : scheduleInPast
                    ? t('WASAPI_CAMPAIGNS.SCHEDULE.PAST')
                    : ''
              "
              :message-type="showError('schedule') ? 'error' : 'info'"
            />
            <p class="text-xs text-n-slate-11">
              {{
                t('WASAPI_CAMPAIGNS.SCHEDULE.TIMEZONE_NOTE', {
                  timezone: accountSettings.timezone,
                })
              }}
            </p>
          </section>
        </div>

        <!-- right column: previews -->
        <aside class="flex flex-col gap-4 lg:sticky lg:top-0 lg:self-start">
          <AudiencePreviewCard
            :preview="audiencePreview"
            :is-loading="isPreviewLoading"
            :empty="!hasAudience"
            :timezone="accountSettings.timezone"
            @refresh="refreshAudiencePreview"
          />
          <div
            class="p-4 rounded-xl outline outline-1 outline-n-weak bg-n-solid-1"
          >
            <MessagePreview
              :channel="channel"
              :template="selectedTemplate"
              :template-params="
                messagePreview?.template_params || templateParams
              "
              :text="messagePreview?.text || form.message"
              :contact="messagePreview?.contact"
              :issues="messagePreview?.issues || []"
            >
              <template #actions>
                <select
                  v-if="previewContactOptions.length"
                  v-model="previewContactId"
                  class="h-7 px-2 text-xs rounded-md bg-n-alpha-2 text-n-slate-12 border-none"
                >
                  <option
                    v-for="option in previewContactOptions"
                    :key="option.value"
                    :value="option.value"
                  >
                    {{ option.label }}
                  </option>
                </select>
              </template>
            </MessagePreview>
          </div>
        </aside>
      </div>
    </div>

    <Dialog
      ref="confirmDialogRef"
      :title="t('WASAPI_CAMPAIGNS.CONFIRM.SCHEDULE_TITLE')"
      :confirm-button-label="t('WASAPI_CAMPAIGNS.ACTIONS.CONFIRM_SCHEDULE')"
      :cancel-button-label="t('WASAPI_CAMPAIGNS.CONFIRM.CANCEL')"
      :is-loading="isSaving"
      width="xl"
      @confirm="confirmSchedule"
    >
      <p
        v-if="largeAudience"
        class="p-3 mb-3 text-sm rounded-lg bg-n-amber-3 text-n-amber-11"
      >
        {{
          t('WASAPI_CAMPAIGNS.CONFIRM.LARGE_AUDIENCE', {
            count: formatNumber(audiencePreview?.eligible, locale),
          })
        }}
      </p>
      <dl class="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-sm">
        <template v-for="[key, value] in summaryRows" :key="key">
          <dt class="text-n-slate-11">
            {{ t(`WASAPI_CAMPAIGNS.SUMMARY.${key}`) }}
          </dt>
          <dd class="text-n-slate-12 truncate">{{ value }}</dd>
        </template>
      </dl>
    </Dialog>
    <TestSendDialog ref="testDialogRef" @send="sendTest" />
    <Dialog
      ref="leaveDialogRef"
      type="alert"
      :title="t('WASAPI_CAMPAIGNS.EDITOR.LEAVE.TITLE')"
      :description="t('WASAPI_CAMPAIGNS.EDITOR.LEAVE.DESCRIPTION')"
      :confirm-button-label="t('WASAPI_CAMPAIGNS.EDITOR.LEAVE.CONFIRM')"
      :cancel-button-label="t('WASAPI_CAMPAIGNS.EDITOR.LEAVE.CANCEL')"
      @confirm="confirmLeave"
      @close="cancelLeave"
    />
  </section>
</template>
